import { useEffect, useState } from "react";
import { ChevronDown, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { COPY_GROUPS } from "../../lib/copyRegistry";
import { inputCls } from "./shared";

export function CopyTab() {
  const [overrides, setOverrides] = useState({});
  const [values, setValues] = useState({});
  const [open, setOpen] = useState(COPY_GROUPS[0]?.id || "");
  const [saving, setSaving] = useState(false);

  const load = () => {
    adminApi().get("/copy").then((r) => {
      setOverrides(r.data || {});
      setValues(r.data || {});
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const dirty = Object.keys(values).filter((k) => (values[k] || "") !== (overrides[k] || ""));

  const save = async () => {
    if (dirty.length === 0) { toast.info("No changes to save"); return; }
    setSaving(true);
    try {
      const updates = {};
      dirty.forEach((k) => { updates[k] = values[k] || ""; });
      const { data } = await adminApi().put("/admin/copy", { updates });
      toast.success(`Saved ${data.saved} change${data.saved === 1 ? "" : "s"}${data.cleared ? `, reverted ${data.cleared}` : ""}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="copy-tab">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-heading font-bold text-slate-900">Site copy</p>
          <p className="text-xs text-slate-500 mt-1">
            Edit any heading or text on the B2B homepage and Farevault /flights. Leave a field empty to use the default text (shown as placeholder). Changes go live instantly.
          </p>
        </div>
        <button
          data-testid="copy-save-btn"
          onClick={save}
          disabled={saving || dirty.length === 0}
          className="shrink-0 bg-[#970C10] hover:bg-[#7A0A0D] disabled:opacity-40 text-white font-bold text-sm px-6 py-2.5 rounded-lg transition-colors duration-200"
        >
          {saving ? "Saving…" : dirty.length > 0 ? `Save ${dirty.length} change${dirty.length === 1 ? "" : "s"}` : "Saved"}
        </button>
      </div>

      {COPY_GROUPS.map((g) => {
        const customized = g.fields.filter((f) => (overrides[f.key] || "") !== "").length;
        const isOpen = open === g.id;
        return (
          <div key={g.id} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <button
              data-testid={`copy-group-${g.id}`}
              onClick={() => setOpen(isOpen ? "" : g.id)}
              className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-slate-50 transition-colors duration-150"
            >
              <span className="font-heading font-bold text-sm text-slate-900">{g.label}</span>
              <span className="flex items-center gap-3">
                {customized > 0 && (
                  <span className="text-[10px] font-bold uppercase tracking-wide bg-red-50 text-[#970C10] border border-red-100 px-2 py-0.5 rounded-full">
                    {customized} customized
                  </span>
                )}
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
              </span>
            </button>
            {isOpen && (
              <div className="px-5 pb-5 pt-1 grid sm:grid-cols-2 gap-4 border-t border-slate-100">
                {g.fields.map((f) => {
                  const val = values[f.key] || "";
                  const changed = val !== (overrides[f.key] || "");
                  return (
                    <div key={f.key} className={f.multiline ? "sm:col-span-2" : ""}>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">
                          {f.label}{changed && <span className="text-[#970C10]"> •</span>}
                        </label>
                        {val && (
                          <button
                            data-testid={`copy-reset-${f.key}`}
                            onClick={() => setValues((v) => ({ ...v, [f.key]: "" }))}
                            className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-400 hover:text-[#970C10] transition-colors duration-200"
                            title="Revert to default"
                          >
                            <RotateCcw className="w-3 h-3" /> default
                          </button>
                        )}
                      </div>
                      {f.multiline ? (
                        <textarea
                          data-testid={`copy-field-${f.key}`}
                          className={`${inputCls} h-20`}
                          value={val}
                          placeholder={f.def}
                          onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                        />
                      ) : (
                        <input
                          data-testid={`copy-field-${f.key}`}
                          className={inputCls}
                          value={val}
                          placeholder={f.def}
                          onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
